import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

const FLUEE_TIMER_KEY = 'fluee_welcome_timer_started';
const FLUEE_TIMER_DURATION = 5 * 60 * 1000;
const FLUEE_DISCOUNT_CODE = 'HG20-K7XQ';
const FLUEE_DISCOUNT_APPLIED_KEY = 'fluee_welcome_discount_applied';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | null} */
  #flueeTimerInterval = null;

  /** @type {MutationObserver | null} */
  #flueeObserver = null;

  /** @type {number | null} */
  #flueeObserverTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#startFlueeTimer
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#stopFlueeTimer
    );

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    this.#applyFlueeDiscount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#startFlueeTimer
    );

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose
    );

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#stopFlueeTimer
    );

    this.#historyAbortController?.abort();
    this.#stopFlueeTimer();
  }

  /**
   * Writes the welcome discount code to the session,
   * once per browsing session.
   *
   * Runs on page load rather than on cart add, so the
   * discount is already present the first time Shopify
   * renders the drawer. This avoids having to force a
   * re-render after the code is applied.
   *
   * The redirect parameter points at cart.js so the
   * request returns a small JSON payload instead of a
   * full page.
   */
  #applyFlueeDiscount() {
    let alreadyApplied = null;

    try {
      alreadyApplied =
        sessionStorage.getItem(
          FLUEE_DISCOUNT_APPLIED_KEY
        );
    } catch (error) {
      return;
    }

    if (alreadyApplied) return;

    try {
      sessionStorage.setItem(
        FLUEE_DISCOUNT_APPLIED_KEY,
        '1'
      );
    } catch (error) {
      return;
    }

    const url =
      `/discount/${encodeURIComponent(
        FLUEE_DISCOUNT_CODE
      )}?redirect=/cart.js`;

    fetch(url, {
      credentials: 'same-origin',
    }).catch(() => {
      /*
       * Let a later page view retry.
       */
      try {
        sessionStorage.removeItem(
          FLUEE_DISCOUNT_APPLIED_KEY
        );
      } catch (error) {
        /* ignore */
      }
    });
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState(
        { cartDrawerOpen: true },
        ''
      );
    }

    this.#historyAbortController =
      new AbortController();

    window.addEventListener(
      'popstate',
      this.#handlePopState,
      {
        signal:
          this.#historyAbortController.signal,
      }
    );
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty(
        '--dialog-drawer-closing-animation',
        'none'
      );

      await this.closeDialog();

      this.refs.dialog.style.removeProperty(
        '--dialog-drawer-closing-animation'
      );
    }
  };

  /**
   * Starts timer only on the first successful cart add.
   */
  #ensureFlueeTimerStarted() {
    const existing =
      localStorage.getItem(FLUEE_TIMER_KEY);

    if (!existing) {
      localStorage.setItem(
        FLUEE_TIMER_KEY,
        Date.now().toString()
      );
    }
  }

  /**
   * Handles successful cart add.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    /*
     * This event only fires after Shopify successfully
     * adds a product to the cart.
     */
    this.#ensureFlueeTimerStarted();

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(
      event.detail.resource?.item_count
    );

    /*
     * Horizon may update drawer HTML after cart add,
     * so wait briefly before injecting the offer.
     */
    setTimeout(() => {
      this.#startFlueeTimer();
    }, 300);
  };

  /**
   * Announces cart count to screen readers.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion =
      /** @type {HTMLElement | undefined} */ (
        this.refs.liveRegion
      );

    if (
      !this.refs.dialog?.open ||
      !liveRegion ||
      cartCount === undefined
    ) {
      return;
    }

    liveRegion.textContent =
      `${Theme.translations.cart_count}: ${cartCount}`;
  }

  /**
   * Removes every offer block currently in the document.
   *
   * Horizon re-renders the drawer after cart updates
   * (adding a discount code, changing quantity), which
   * can orphan a previously injected block. Searching
   * the whole document guarantees no stale copy is left
   * behind to overlap the checkout button.
   */
  #removeFlueeOffers() {
    document
      .querySelectorAll('#fluee-welcome-offer')
      .forEach((node) => node.remove());
  }

  /**
   * Returns true when exactly one offer block exists
   * and it sits immediately before the current summary.
   */
  #isFlueeOfferPlaced() {
    const dialog = this.refs.dialog;

    if (!dialog) return false;

    const offers =
      document.querySelectorAll(
        '#fluee-welcome-offer'
      );

    if (offers.length !== 1) return false;

    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    if (!summary) return false;

    return (
      offers[0].nextElementSibling === summary
    );
  }

  /**
   * Re-injects the offer if a drawer re-render
   * removed or displaced it.
   */
  #watchFlueeRerender() {
    const dialog = this.refs.dialog;

    if (!dialog || this.#flueeObserver) return;

    this.#flueeObserver =
      new MutationObserver(() => {
        if (
          this.#flueeObserverTimeout !== null
        ) {
          window.clearTimeout(
            this.#flueeObserverTimeout
          );
        }

        this.#flueeObserverTimeout =
          window.setTimeout(() => {
            this.#flueeObserverTimeout = null;

            if (!this.#isFlueeOfferPlaced()) {
              this.#startFlueeTimer();
            }
          }, 150);
      });

    this.#flueeObserver.observe(dialog, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Adds offer UI to cart drawer if it does not exist.
   */
  #createFlueeOffer() {
    const dialog = this.refs.dialog;

    if (!dialog) return null;

    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    if (!summary) {
      return null;
    }

    if (this.#isFlueeOfferPlaced()) {
      return document.querySelector(
        '#fluee-welcome-offer'
      );
    }

    this.#removeFlueeOffers();

    const offer =
      document.createElement('div');

    offer.id = 'fluee-welcome-offer';

    offer.innerHTML = `
      <div class="fluee-welcome-offer__left">
        <div class="fluee-welcome-offer__title">
          İlk siparişinize %20 hoş geldin avantajı
        </div>

        <div class="fluee-welcome-offer__description">
          Yalnızca ilk siparişinizde geçerli.
        </div>
      </div>

      <div
        class="fluee-welcome-offer__timer"
        aria-live="polite"
      >
        05:00
      </div>
    `;

    /*
     * Insert directly before checkout summary.
     */
    summary.parentNode?.insertBefore(
      offer,
      summary
    );

    this.#addFlueeStyles();

    return offer;
  }

  /**
   * Adds styles once.
   */
  #addFlueeStyles() {
    if (
      document.getElementById(
        'fluee-welcome-offer-style'
      )
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id =
      'fluee-welcome-offer-style';

    style.textContent = `
  #fluee-welcome-offer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;

    width: auto;
    box-sizing: border-box;

    margin: 0 48px;
    padding: 18px 0;

    border-top: 1px solid rgba(0, 0, 0, 0.15);
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  }

  #fluee-welcome-offer .fluee-welcome-offer__left {
    flex: 1 1 auto;
    min-width: 0;
  }

  #fluee-welcome-offer .fluee-welcome-offer__title {
    margin: 0 0 5px;

    font-size: 14px;
    line-height: 1.35;
    font-weight: 500;

    overflow-wrap: anywhere;
  }

  #fluee-welcome-offer .fluee-welcome-offer__description {
    margin: 0;

    font-size: 12px;
    line-height: 1.4;

    opacity: 0.6;
  }

  #fluee-welcome-offer .fluee-welcome-offer__timer {
    flex: 0 0 auto;

    min-width: 64px;
    text-align: right;

    font-size: 22px;
    line-height: 1;

    font-weight: 400;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
  }

  @media screen and (max-width: 749px) {
    #fluee-welcome-offer {
      margin: 0 24px;
      gap: 16px;
      padding: 16px 0;
    }

    #fluee-welcome-offer .fluee-welcome-offer__title {
      font-size: 13px;
    }

    #fluee-welcome-offer .fluee-welcome-offer__description {
      font-size: 11px;
    }

    #fluee-welcome-offer .fluee-welcome-offer__timer {
      min-width: 58px;
      font-size: 20px;
    }
  }
`;

    document.head.appendChild(style);
  }

  /**
   * Starts / updates countdown.
   */
  #startFlueeTimer = () => {
    this.#stopFlueeTimer();

    const startedAtString =
      localStorage.getItem(FLUEE_TIMER_KEY);

    if (!startedAtString) {
      this.#removeFlueeOffers();

      return;
    }

    const startedAt =
      Number(startedAtString);

    if (!Number.isFinite(startedAt)) {
      localStorage.removeItem(
        FLUEE_TIMER_KEY
      );

      this.#removeFlueeOffers();

      return;
    }

    /*
     * Already expired: never inject the block,
     * so no stale countdown can appear.
     */
    if (
      Date.now() - startedAt >=
      FLUEE_TIMER_DURATION
    ) {
      this.#removeFlueeOffers();

      return;
    }

    const offer =
      this.#createFlueeOffer();

    if (!offer) {
      return;
    }

    const timer =
      offer.querySelector(
        '.fluee-welcome-offer__timer'
      );

    if (!timer) {
      return;
    }

    const update = () => {
      const elapsed =
        Date.now() - startedAt;

      const remaining =
        FLUEE_TIMER_DURATION - elapsed;

      if (remaining <= 0) {
        this.#stopFlueeTimer();

        this.#removeFlueeOffers();

        return;
      }

      const totalSeconds =
        Math.ceil(remaining / 1000);

      const minutes =
        Math.floor(
          totalSeconds / 60
        );

      const seconds =
        totalSeconds % 60;

      timer.textContent =
        `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    update();

    this.#flueeTimerInterval =
      window.setInterval(
        update,
        1000
      );

    this.#watchFlueeRerender();
  };

  /**
   * Stops countdown interval and re-render watcher.
   */
  #stopFlueeTimer = () => {
    if (
      this.#flueeTimerInterval !== null
    ) {
      window.clearInterval(
        this.#flueeTimerInterval
      );

      this.#flueeTimerInterval = null;
    }

    if (
      this.#flueeObserverTimeout !== null
    ) {
      window.clearTimeout(
        this.#flueeObserverTimeout
      );

      this.#flueeObserverTimeout = null;
    }

    this.#flueeObserver?.disconnect();
    this.#flueeObserver = null;
  };

  open() {
    this.showDialog();

    setTimeout(() => {
      this.#startFlueeTimer();
    }, 200);

    /**
     * Close cart drawer when installments CTA
     * is clicked to avoid overlapping dialogs.
     */
    customElements
      .whenDefined(
        'shopify-payment-terms'
      )
      .then(() => {
        const installmentsContent =
          document.querySelector(
            'shopify-payment-terms'
          )?.shadowRoot;

        const cta =
          installmentsContent?.querySelector(
            '#shopify-installments-cta'
          );

        cta?.addEventListener(
          'click',
          this.closeDialog,
          { once: true }
        );
      });
  }

  close() {
    this.#stopFlueeTimer();
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } =
      /** @type {Refs} */ (
        this.refs
      );

    if (!dialog) return;

    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );

    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect()
        .height;

    const summaryHeight =
      summary.getBoundingClientRect()
        .height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}